import React, { useState, useEffect, useMemo } from 'react';
import { ControlObligation, StatutoryProvision } from '../types/obligation';
import { ApplicabilityDecision, DecisionStatus } from '../types/decision';
import { ArchitectureSystem } from '../types/system';
import { ComplianceUser } from '../types/user';
import { StatutoryExceptionRecord } from '../types/exception';
import { formatCitation, getTrancheInfo } from '../utils/citationFormatter';
import { getGovernmentLinkage } from '../utils/governmentDocumentLookup';
import penaltySchedule from '../data/penalty-schedule.json';
import {
  X,
  Scale,
  Layers,
  CheckCircle2,
  XCircle,
  AlertCircle,
  HelpCircle,
  ExternalLink,
  ShieldAlert,
  Cpu,
  FileCheck,
  Calendar,
  Save,
  UserCheck,
  AlertTriangle,
  Info,
  BookOpen,
  HelpCircle as QuestionIcon,
  CheckSquare,
  FileText,
  Sparkles,
  Plus,
  Server,
  Landmark,
  Mail,
  Phone,
  Briefcase,
  Users,
  Zap,
  Shield
} from 'lucide-react';

interface ObligationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: ControlObligation | StatutoryProvision | null;
  isControl: boolean;
  decision?: ApplicabilityDecision;
  customSystems: ArchitectureSystem[];
  owners?: string[];
  users?: ComplianceUser[];
  exceptions?: StatutoryExceptionRecord[];
  onSaveDecision: (decision: ApplicabilityDecision) => void;
  onOpenKnowledgeBase?: (tab: 'exemptions' | 'actors' | 'systems' | 'penalties') => void;
  onOpenSystemsInventory?: () => void;
}

const OWNER_PRESETS = [
  'General Counsel / Legal',
  'CISO / Security Operations',
  'Data Protection Officer (DPO)',
  'Product Engineering & Architecture',
  'Procurement & Vendor Risk (TPRM)',
  'HR & Employee Data Operations',
  'Marketing & Growth Operations'
];

export default function ObligationDrawer({
  isOpen,
  onClose,
  item,
  isControl,
  decision,
  customSystems,
  owners,
  users,
  exceptions,
  onSaveDecision,
  onOpenKnowledgeBase,
  onOpenSystemsInventory
}: ObligationDrawerProps) {
  if (!isOpen || !item) return null;

  const id = isControl ? (item as ControlObligation).id : (item as StatutoryProvision).id;
  const title = isControl
    ? (item as ControlObligation).title || (item as ControlObligation).name
    : formatCitation((item as StatutoryProvision).provision);

  const domainLabel = isControl
    ? (item as ControlObligation).domain || (item as ControlObligation).domain_title || 'General Governance'
    : (item as StatutoryProvision).actor;

  const defaultSystemsStr = isControl ? (item as ControlObligation).systems || '' : '';
  const defaultSystemsList = defaultSystemsStr.split(';').map(s => s.trim()).filter(Boolean);

  const commencement = isControl
    ? (item as ControlObligation).legal_source_records?.[0]?.commencement || '2027-05-13'
    : (item as StatutoryProvision).commencement;
  const tranche = getTrancheInfo(commencement);
  
  // Government Published Document Linkage
  const govLinkage = getGovernmentLinkage(item, isControl);

  // Form State
  const [status, setStatus] = useState<DecisionStatus>(decision?.status || 'UNASSESSED');
  const [entityFacts, setEntityFacts] = useState(decision?.entity_facts || '');
  const [rationale, setRationale] = useState(decision?.rationale || '');
  const [assignedOwner, setAssignedOwner] = useState(
    decision?.assigned_owner || (isControl ? (item as ControlObligation).recommended_owner : 'General Counsel / Legal')
  );
  const [reviewerName, setReviewerName] = useState(decision?.reviewer_name || 'Compliance Lead');
  const [reviewDate, setReviewDate] = useState(decision?.review_date || new Date().toISOString().slice(0, 10));
  const [selectedSystems, setSelectedSystems] = useState<string[]>(() => {
    if (decision?.systems_in_scope && decision.systems_in_scope.length > 0) {
      return decision.systems_in_scope;
    }
    return defaultSystemsList;
  });

  const [activeTab, setActiveTab] = useState<'decision' | 'playbook' | 'statutory' | 'control'>('decision');
  const [isSavedNotice, setIsSavedNotice] = useState(false);
  const [factToast, setFactToast] = useState<{ message: string; type: 'add' | 'remove' } | null>(null);

  useEffect(() => {
    setStatus(decision?.status || 'UNASSESSED');
    setEntityFacts(decision?.entity_facts || '');
    setRationale(decision?.rationale || '');
    setAssignedOwner(
      decision?.assigned_owner || (isControl ? (item as ControlObligation).recommended_owner : 'General Counsel / Legal')
    );
    setReviewerName(decision?.reviewer_name || 'Compliance Lead');
    setReviewDate(decision?.review_date || new Date().toISOString().slice(0, 10));
    setSelectedSystems(decision?.systems_in_scope && decision.systems_in_scope.length > 0 ? decision.systems_in_scope : defaultSystemsList);
  }, [decision, item]);

  const matchedUser = useMemo(() => {
    if (!users || users.length === 0) return null;
    return (
      users.find(u => reviewerName.toLowerCase().includes(u.first_name.toLowerCase()) || reviewerName.toLowerCase().includes(u.email.toLowerCase())) ||
      users.find(u => assignedOwner.toLowerCase().includes(u.first_name.toLowerCase()) || (u.mapped_roles || []).some(r => r.toLowerCase() === assignedOwner.toLowerCase())) ||
      null
    );
  }, [users, reviewerName, assignedOwner]);

  const matchedExceptions = useMemo(() => {
    if (!exceptions || exceptions.length === 0) return [];
    return exceptions.filter(ex => (ex.mapped_obligation_ids || []).includes(id));
  }, [exceptions, id]);

  const handleToggleSystem = (sysId: string) => {
    setSelectedSystems(prev => 
      prev.includes(sysId) ? prev.filter(s => s !== sysId) : [...prev, sysId]
    );
  };

  const handleSave = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const updated: ApplicabilityDecision = {
      id,
      target_id: id,
      target_type: isControl ? 'control' : 'provision',
      status,
      entity_facts: entityFacts,
      rationale,
      assigned_owner: assignedOwner,
      reviewer_name: reviewerName,
      review_date: reviewDate,
      unresolved_blockers: decision?.unresolved_blockers || [],
      systems_in_scope: selectedSystems,
      updated_at: new Date().toISOString(),
      rev: (decision?.rev || 0) + 1
    };

    onSaveDecision(updated);
    setIsSavedNotice(true);
    setTimeout(() => setIsSavedNotice(false), 2000);
  };

  const handleToggleFact = (fact: string) => {
    if (entityFacts.includes(fact)) {
      const lines = entityFacts.split('\n');
      const filtered = lines.filter(line => !line.includes(fact));
      setEntityFacts(filtered.join('\n').trim());
      setFactToast({ message: `Removed fact from decision notes: "${fact}"`, type: 'remove' });
    } else {
      setEntityFacts(prev => (prev && prev.trim() ? `${prev.trim()}\n• ${fact}` : `• ${fact}`));
      setFactToast({ message: `Added fact to decision notes: "${fact}"`, type: 'add' });
    }
    setTimeout(() => {
      setFactToast(null);
    }, 3500);
  };

  // Find relevant penalty if applicable
  const matchedPenalty = penaltySchedule.find(p => {
    const provStr = isControl ? (item as ControlObligation).scope : (item as StatutoryProvision).provision;
    return provStr && provStr.toLowerCase().includes(p.section.toLowerCase());
  }) || penaltySchedule.find(p => p.section === 'General');

  const questions = isControl
    ? (item as ControlObligation).assessment_questions || []
    : [
        `Does your organization perform processing that triggers ${formatCitation((item as StatutoryProvision).provision)}?`,
        `Are you acting as a ${(item as StatutoryProvision).actor} for this processing purpose?`,
        `Do any statutory exemptions under Section 17 apply to this activity?`
      ];

  const requiredFacts = isControl
    ? (item as ControlObligation).required_facts || []
    : [
        "Classification of digital personal data categories",
        "Geographical nexus with Data Principals within India",
        "Lawful processing ground (Consent vs Legitimate Uses s.7)"
      ];

  const auditArtifacts = isControl
    ? (item as ControlObligation).audit_artifacts || []
    : [
        "Statutory notice and consent record",
        "DPO review and sign-off workpaper"
      ];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col h-full z-10 transition-all">
        
        {/* Drawer Header */}
        <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-4 bg-slate-50/70 dark:bg-slate-900/90">
          <div className="space-y-1.5 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-lg text-xs font-mono font-extrabold bg-accent/15 text-accent dark:text-accent-light border border-accent/30">
                {id}
              </span>
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono font-bold border ${tranche.badgeClass}`}>
                {tranche.tranche} • {tranche.date}
              </span>
              {matchedPenalty && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30 flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3" />
                  <span>Max Penalty: {matchedPenalty.max_penalty_display}</span>
                </span>
              )}
            </div>

            <h3 className="text-lg font-extrabold text-slate-900 dark:text-slate-100 tracking-tight leading-snug">
              {title}
            </h3>
            <div className="flex items-center gap-2 text-xs font-semibold text-accent dark:text-accent-light">
              <span>{domainLabel}</span>
            </div>

            {/* Government Published Document Badge in Header */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <a
                href={govLinkage.officialUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition-colors shadow-2xs"
                title={`${govLinkage.officialTitle} (${govLinkage.gazetteNotification}) • Click to view official PDF`}
              >
                <Landmark className="w-3.5 h-3.5 text-accent" />
                <span className="font-bold">{govLinkage.shortTitle}</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
              <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                {govLinkage.gazetteNotification}
              </span>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 px-6 bg-slate-50/50 dark:bg-slate-900/50 overflow-x-auto">
          <button
            onClick={() => setActiveTab('decision')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'decision'
                ? 'border-accent text-accent dark:text-accent-light'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Applicability Decision</span>
          </button>

          <button
            onClick={() => setActiveTab('playbook')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'playbook'
                ? 'border-accent text-accent dark:text-accent-light'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-accent" />
            <span>Assessment Playbook & Facts</span>
          </button>

          <button
            onClick={() => setActiveTab('statutory')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === 'statutory'
                ? 'border-accent text-accent dark:text-accent-light'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Scale className="w-3.5 h-3.5" />
            <span>Statutory Gazette</span>
          </button>

          {isControl && (
            <button
              onClick={() => setActiveTab('control')}
              className={`py-3 px-3 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap ${
                activeTab === 'control'
                  ? 'border-accent text-accent dark:text-accent-light'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Cpu className="w-3.5 h-3.5" />
              <span>Control Architecture</span>
            </button>
          )}
        </div>

        {/* Drawer Body Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* TAB 1: DECISION FORM */}
          {activeTab === 'decision' && (
            <form onSubmit={handleSave} className="space-y-5">
              
              {/* Official Government Published Document Authority Card */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/80 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-accent/15 text-accent flex items-center justify-center">
                      <Landmark className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-bold text-slate-900 dark:text-slate-100">
                      Government Published Document Linkage
                    </span>
                  </div>
                  <a
                    href={govLinkage.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-accent hover:underline flex items-center gap-1"
                  >
                    <span>Official Gazette PDF</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-0.5">
                    <div className="text-[10px] font-mono text-slate-400 uppercase">Primary Instrument</div>
                    <div className="font-semibold text-slate-800 dark:text-slate-200 truncate" title={govLinkage.officialTitle}>
                      {govLinkage.shortTitle}
                    </div>
                    <div className="text-[11px] text-slate-500">{govLinkage.gazetteNotification}</div>
                  </div>

                  <div className="p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 space-y-0.5">
                    <div className="text-[10px] font-mono text-slate-400 uppercase">Gazette Publication</div>
                    <div className="font-semibold text-slate-800 dark:text-slate-200">
                      {govLinkage.instrumentDate}
                    </div>
                    <div className="text-[10px] font-mono text-slate-400 truncate" title={`SHA-256: ${govLinkage.sha256}`}>
                      SHA: {govLinkage.sha256.slice(0, 16)}...
                    </div>
                  </div>
                </div>
              </div>

              {/* Statutory Safe Harbour Exemption Banner */}
              {matchedExceptions.length > 0 && (
                <div className="p-4 rounded-2xl bg-amber-50/70 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800/80 space-y-2.5 animate-fade-in">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="text-xs font-bold text-amber-900 dark:text-amber-200">
                        Statutory Safe Harbour Available ({matchedExceptions.length})
                      </span>
                    </div>
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-amber-200/80 dark:bg-amber-900 text-amber-900 dark:text-amber-200">
                      {matchedExceptions[0].statutory_ground}
                    </span>
                  </div>
                  <p className="text-xs text-amber-900/90 dark:text-amber-200/90 leading-relaxed">
                    <strong>{matchedExceptions[0].title}:</strong> {matchedExceptions[0].justification}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setStatus('NOT_APPLICABLE');
                      setRationale(`Exempt under ${matchedExceptions[0].statutory_ground} (${matchedExceptions[0].title}): ${matchedExceptions[0].justification}`);
                      setReviewerName(matchedExceptions[0].approved_by || reviewerName);
                      if (!entityFacts.includes(matchedExceptions[0].statutory_ground)) {
                        setEntityFacts(prev => prev ? `${prev}\n• Statutory Exception: ${matchedExceptions[0].statutory_ground} - ${matchedExceptions[0].title}` : `• Statutory Exception: ${matchedExceptions[0].statutory_ground} - ${matchedExceptions[0].title}`);
                      }
                    }}
                    className="w-full py-2 px-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-colors shadow-xs"
                  >
                    <Zap className="w-3.5 h-3.5" />
                    <span>Apply Safe Harbour Exemption ({matchedExceptions[0].statutory_ground})</span>
                  </button>
                </div>
              )}

              {/* Status Selector */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center justify-between">
                  <span>Applicability Classification State</span>
                  <span className="text-[11px] text-slate-500 font-normal">Deterministic evaluation</span>
                </label>

                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setStatus('APPLICABLE')}
                    className={`py-2.5 px-3 rounded-xl border text-xs font-bold flex flex-col items-center gap-1 transition-all ${
                      status === 'APPLICABLE'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-md ring-2 ring-emerald-500/20'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-500'
                    }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Applicable</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setStatus('NOT_APPLICABLE')}
                    className={`py-2.5 px-3 rounded-xl border text-xs font-bold flex flex-col items-center gap-1 transition-all ${
                      status === 'NOT_APPLICABLE'
                        ? 'bg-slate-600 text-white border-slate-600 shadow-md ring-2 ring-slate-500/20'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-500'
                    }`}
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Not Applicable</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setStatus('UNRESOLVED')}
                    className={`py-2.5 px-3 rounded-xl border text-xs font-bold flex flex-col items-center gap-1 transition-all ${
                      status === 'UNRESOLVED'
                        ? 'bg-amber-600 text-white border-amber-600 shadow-md ring-2 ring-amber-500/20'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-amber-500'
                    }`}
                  >
                    <AlertCircle className="w-4 h-4" />
                    <span>Unresolved</span>
                  </button>
                </div>
              </div>

              {/* Target Systems Multi-Select Mapping */}
              <div className="space-y-2 p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                    <Server className="w-3.5 h-3.5 text-indigo-500" />
                    <span>Target Enterprise Architecture Systems in Scope</span>
                  </label>
                  {onOpenSystemsInventory && (
                    <button
                      type="button"
                      onClick={onOpenSystemsInventory}
                      className="text-[11px] font-bold text-accent hover:underline flex items-center gap-1"
                    >
                      <span>Manage Systems</span>
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5 pt-1 max-h-32 overflow-y-auto">
                  {customSystems.map((sys) => {
                    const isSelected = selectedSystems.includes(sys.id);
                    return (
                      <button
                        key={sys.id}
                        type="button"
                        onClick={() => handleToggleSystem(sys.id)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-mono transition-all border flex items-center gap-1.5 ${
                          isSelected
                            ? 'bg-indigo-600 text-white border-indigo-600 font-bold shadow-xs'
                            : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-indigo-400'
                        }`}
                      >
                        <span>{isSelected ? '✓' : '+'}</span>
                        <span className="font-bold">{sys.id}</span>
                        <span className="text-[10px] opacity-80 truncate max-w-[120px]">{sys.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Entity Facts Context */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-accent" />
                    <span>Entity Facts & Activity Scope</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setActiveTab('playbook')}
                    className="text-[11px] text-accent hover:underline flex items-center gap-1 font-medium"
                  >
                    <span>View Assessment Playbook</span>
                    <Sparkles className="w-3 h-3" />
                  </button>
                </div>
                <textarea
                  rows={3}
                  value={entityFacts}
                  onChange={(e) => setEntityFacts(e.target.value)}
                  placeholder="Record entity facts: data category processed, volume, offshore nexus, child data presence, processor engagement..."
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-accent"
                />

                {/* Quick Add Fact Chips from Assessment Playbook */}
                {requiredFacts.length > 0 && (
                  <div className="space-y-1.5 pt-0.5">
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-between">
                      <span className="flex items-center gap-1 font-medium">
                        <Sparkles className="w-3 h-3 text-accent" />
                        <span>Suggested facts (click to toggle in notes):</span>
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {requiredFacts.filter(f => entityFacts.includes(f)).length}/{requiredFacts.length} added
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {requiredFacts.map((fact, idx) => {
                        const isAdded = entityFacts.includes(fact);
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => handleToggleFact(fact)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 text-left ${
                              isAdded
                                ? 'bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 font-semibold shadow-xs'
                                : 'bg-slate-100/80 dark:bg-slate-800/80 hover:bg-slate-200/80 dark:hover:bg-slate-700/80 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {isAdded ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            ) : (
                              <Plus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            )}
                            <span className="truncate max-w-sm">{fact}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Compliance & Legal Rationale */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center justify-between">
                  <span>Legal Review Rationale & Interpretation</span>
                  <span className="text-[11px] text-slate-500 font-normal">Counsel / DPO justification</span>
                </label>
                <textarea
                  rows={3}
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Provide statutory basis for decision, applicable exemptions (e.g. s.17), or conditions required to achieve compliance..."
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-accent"
                />
              </div>

              {/* Control Ownership Assignment */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center justify-between">
                  <span>Assigned Internal Control Owner</span>
                  <span className="text-[11px] text-slate-500 font-normal">Accountable role</span>
                </label>
                <input
                  type="text"
                  value={assignedOwner}
                  onChange={(e) => setAssignedOwner(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  placeholder="e.g. CISO / Security Operations Team"
                />
                
                {/* Preset Chips */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(owners && owners.length > 0 ? owners : OWNER_PRESETS).map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setAssignedOwner(preset)}
                      className="px-2 py-0.5 rounded-md text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      + {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Personnel Quick Assignment Picker */}
              {users && users.length > 0 && (
                <div className="space-y-1.5 p-3 rounded-xl bg-purple-50/60 dark:bg-purple-950/30 border border-purple-200/80 dark:border-purple-800/60">
                  <div className="flex items-center justify-between text-[11px] font-bold text-purple-900 dark:text-purple-200">
                    <span className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                      <span>Assign Registered Compliance Personnel</span>
                    </span>
                    <span className="text-[10px] text-purple-500 font-mono">{users.length} available</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {users.map((u) => {
                      const isAssigned = reviewerName.includes(u.first_name) || assignedOwner.includes(u.first_name) || (u.mapped_roles || []).includes(assignedOwner);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            if (u.mapped_roles && u.mapped_roles.length > 0) {
                              setAssignedOwner(u.mapped_roles[0]);
                            } else {
                              setAssignedOwner(`${u.first_name} ${u.last_name} (${u.designation})`);
                            }
                            setReviewerName(`${u.first_name} ${u.last_name} (${u.designation})`);
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                            isAssigned
                              ? 'bg-purple-600 text-white shadow-xs'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-purple-200 dark:border-purple-800 hover:border-purple-400'
                          }`}
                        >
                          <span className="w-4 h-4 rounded-full bg-purple-200 dark:bg-purple-700 text-purple-800 dark:text-purple-100 font-bold text-[9px] flex items-center justify-center">
                            {u.first_name[0]}{u.last_name[0]}
                          </span>
                          <span>{u.first_name} {u.last_name}</span>
                          <span className="text-[10px] opacity-75 font-normal">({u.designation})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reviewer & Date */}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Reviewer Name
                  </label>
                  <input
                    type="text"
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Sign-Off Date
                  </label>
                  <input
                    type="date"
                    value={reviewDate}
                    onChange={(e) => setReviewDate(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-900 dark:text-slate-100 focus:outline-none focus:border-accent"
                  />
                </div>
              </div>

              {/* Matched Personnel Contact Details Card */}
              {matchedUser && (
                <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {matchedUser.first_name} {matchedUser.last_name} • {matchedUser.department}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] font-mono text-slate-600 dark:text-slate-400">
                    <a href={`mailto:${matchedUser.email}`} className="flex items-center gap-1 hover:text-accent underline">
                      <Mail className="w-3 h-3 text-indigo-500" />
                      <span>{matchedUser.email}</span>
                    </a>
                    {matchedUser.phone && (
                      <a href={`tel:${matchedUser.phone}`} className="flex items-center gap-1 hover:text-emerald-500">
                        <Phone className="w-3 h-3 text-emerald-500" />
                        <span>{matchedUser.phone}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="pt-4 flex items-center justify-between border-t border-slate-200 dark:border-slate-800">
                <div className="text-xs text-slate-500 font-mono">
                  {isSavedNotice ? (
                    <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Saved to local IndexedDB
                    </span>
                  ) : (
                    <span>Auto-saves locally with audit record</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-all shadow-md shadow-accent/20"
                >
                  <Save className="w-4 h-4" />
                  <span>Save Decision</span>
                </button>
              </div>

            </form>
          )}

          {/* TAB 2: IN-APP ASSESSMENT PLAYBOOK */}
          {activeTab === 'playbook' && (
            <div className="space-y-5">
              
              {/* Key Assessment Questions */}
              <div className="p-4 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900/40 space-y-2.5">
                <div className="text-xs font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5">
                  <QuestionIcon className="w-4 h-4" />
                  <span>Key Applicability Evaluation Questions</span>
                </div>
                <div className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300">
                  {questions.map((q, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-white dark:bg-slate-900 p-2.5 rounded-xl border border-indigo-100 dark:border-indigo-950">
                      <span className="font-mono font-bold text-indigo-600">{idx + 1}.</span>
                      <span className="leading-relaxed">{q}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fact Addition Toast/Banner */}
              {factToast && (
                <div className={`p-3 rounded-xl text-xs flex items-center justify-between gap-3 border transition-all animate-in fade-in duration-200 ${
                  factToast.type === 'add'
                    ? 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-300 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200'
                    : 'bg-amber-50 dark:bg-amber-950/60 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-200'
                }`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {factToast.type === 'add' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                    ) : (
                      <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    )}
                    <span className="font-semibold truncate">{factToast.message}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('decision')}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 shrink-0 flex items-center gap-1 shadow-xs"
                  >
                    <span>View in Decision Tab</span>
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Required Entity Facts Checklist */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                    <CheckSquare className="w-4 h-4 text-accent" />
                    <span>Required Entity Facts Checklist</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-medium">Click any fact to toggle in your decision notes</span>
                </div>

                <div className="space-y-2">
                  {requiredFacts.map((fact, idx) => {
                    const isAdded = entityFacts.includes(fact);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleToggleFact(fact)}
                        className={`w-full text-left p-3 rounded-xl border text-xs transition-all flex items-center justify-between group ${
                          isAdded
                            ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-400 dark:border-emerald-700/80 text-emerald-950 dark:text-emerald-100 font-medium shadow-xs'
                            : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-accent dark:hover:border-accent text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="flex items-start gap-2.5 pr-2">
                          {isAdded ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                          ) : (
                            <Plus className="w-4 h-4 text-slate-400 group-hover:text-accent shrink-0 mt-0.5" />
                          )}
                          <span className="leading-snug">{fact}</span>
                        </div>
                        <div className="shrink-0 flex items-center gap-1.5 text-[11px] font-bold">
                          {isAdded ? (
                            <span className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                              ✓ In Notes
                            </span>
                          ) : (
                            <span className="text-accent opacity-80 group-hover:opacity-100 flex items-center gap-0.5">
                              + Add to Notes
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Statutory Exemptions Quick Checker */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                    <Scale className="w-4 h-4 text-emerald-600" />
                    <span>Statutory Exemptions (Section 17 & Section 3)</span>
                  </div>
                  {onOpenKnowledgeBase && (
                    <button
                      type="button"
                      onClick={() => onOpenKnowledgeBase('exemptions')}
                      className="text-[11px] text-accent hover:underline flex items-center gap-0.5"
                    >
                      <span>Full Guide</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Verify if your activity qualifies under Section 17(1)(a)-(f) (legal claims, court order, foreign data contract) or Section 3(c) (domestic use, public data).
                </p>
              </div>

              {/* Audit Artifacts Requirements */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-2">
                <div className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-accent" />
                  <span>Required Audit Evidence Artifacts</span>
                </div>
                <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
                  {auditArtifacts.map((art, idx) => (
                    <li key={idx} className="flex items-start gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{art}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          )}

          {/* TAB 3: STATUTORY GAZETTE PASSAGE */}
          {activeTab === 'statutory' && (
            <div className="space-y-5">
              
              {/* Canonical Gazette Passage Box */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-slate-200/80 dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-accent/15 text-accent flex items-center justify-center">
                      <Landmark className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-slate-900 dark:text-slate-100">
                        {govLinkage.shortTitle}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        {govLinkage.gazetteNotification} • {govLinkage.instrumentDate}
                      </div>
                    </div>
                  </div>

                  <a
                    href={govLinkage.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-accent dark:text-accent-light bg-accent/10 hover:bg-accent/20 border border-accent/20 transition-colors self-start sm:self-auto"
                  >
                    <span>Official Gazette PDF</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <pre className="text-xs font-serif leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-wrap bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200/80 dark:border-slate-800 max-h-96 overflow-y-auto">
                  {(item as any).exact_passage || (item as any).control}
                </pre>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between text-[11px] text-slate-400 dark:text-slate-500 font-mono pt-1 gap-1">
                  <span>Gazette lines: {govLinkage.lineStart || (item as any).line_start || 1} — {govLinkage.lineEnd || (item as any).line_end || 'end'}</span>
                  <span className="truncate max-w-xs" title={`SHA-256: ${govLinkage.sha256}`}>
                    SHA-256: {govLinkage.sha256.slice(0, 20)}...
                  </span>
                </div>
              </div>

              {/* Statutory Conditions & Exceptions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-1">
                  <div className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase">
                    Statutory Conditions
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {(item as any).conditions || 'Subject to general statutory obligations.'}
                  </p>
                </div>

                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 space-y-1">
                  <div className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase">
                    Statutory Exceptions
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    {(item as any).exception || 'Standard Section 17 exemptions apply if satisfied.'}
                  </p>
                </div>
              </div>

              {/* Practical Guidance */}
              <div className="p-4 rounded-xl bg-accent/5 dark:bg-accent/10 border border-accent/20 space-y-1.5">
                <div className="text-xs font-bold text-accent dark:text-accent-light flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" />
                  <span>Authoritative Guidance & Boundary</span>
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                  {(item as any).author_interpretation || (item as any).description || 'Verify statutory trigger before deploying technical enforcement.'}
                </p>
              </div>

            </div>
          )}

          {/* TAB 4: CONTROL ARCHITECTURE */}
          {activeTab === 'control' && isControl && (
            <div className="space-y-4">
              
              {/* Architecture Systems */}
              <div className="p-4 rounded-xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-900/40 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5">
                    <Cpu className="w-4 h-4" />
                    <span>Mapped Architecture Systems</span>
                  </div>
                  {onOpenSystemsInventory && (
                    <button
                      type="button"
                      onClick={onOpenSystemsInventory}
                      className="text-[11px] text-accent hover:underline"
                    >
                      Manage Systems Inventory
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5 pt-1">
                  {selectedSystems.map((sysId) => {
                    const found = customSystems.find(s => s.id === sysId);
                    return (
                      <span
                        key={sysId}
                        className="px-2 py-1 rounded-lg text-xs font-mono font-bold bg-white dark:bg-slate-900 border border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 flex items-center gap-1"
                      >
                        <span>{sysId}</span>
                        {found && <span className="text-[10px] font-normal text-slate-500">({found.name})</span>}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Test Specification */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-2">
                <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                  <FileCheck className="w-4 h-4 text-accent" />
                  <span>Assurance Test Specification</span>
                </div>
                <div className="font-mono text-xs font-semibold text-accent">
                  {(item as ControlObligation).test_id}
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  {(item as ControlObligation).expected}
                </p>
              </div>

              {/* Evidence Workpaper */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-1">
                <div className="text-xs font-bold text-slate-800 dark:text-slate-200">
                  Populated Workpaper Dossier
                </div>
                <div className="font-mono text-xs text-slate-500 dark:text-slate-400">
                  {(item as ControlObligation).evidence}
                </div>
              </div>

            </div>
          )}

        </div>

      </div>
    </div>
  );
}
